import { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Modal, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import Select from '../../../components/textFields/AppSelect'
import axios from 'axios'
import { Autocomplete, Libraries, useJsApiLoader } from '@react-google-maps/api'
import { KTSVG } from '../../../_metronic/helpers'
import TextField from '../../../components/textFields/TextField'
import SearchSelect from '../../../components/textFields/SearchSelect'
import { useGetUserDraftByIdQuery, useUpdateUserDraftMutation } from '../../../services/DraftApi'
import {
    useDepartmentsLookupQuery,
    useGetAllDesignationWithoutRoleQuery,
    useGetAllDivisionQuery,
    useGetAllLocationsQuery,
    useGetAllLst_UserExpenseTypeQuery,
    useGetAllTimeZoneQuery,
    useGetAllUserTypeLookupQuery,
} from '../../../services/GeneralApi'
import { baseUrl } from '../../../services/baseUrl'
import labelKey from '../../localization/label.json'
import { useAppSelector } from '../../../hooks'
import { selectAuth } from '../../../features/authSlice'

type Props = {
    show: boolean
    handleClose: () => void
    data: any
    onSaved?: () => void
}

const FALLBACK_EXPENSE_ACCOUNT_TYPE_OPTIONS = [
    { value: 1, label: 'Flex' },
    { value: 2, label: 'Regular' },
    { value: 3, label: 'Both (Flex & Regular)' },
]

const FALLBACK_USER_TYPE_OPTIONS = [
    { value: 4, label: 'Manager' },
    { value: 2, label: 'Provider' },
    { value: 5, label: 'Employee' },
]

const normalizeId = (id: any) => {
    if (id == null || id === '' || Number(id) === 0) return null
    return id
}

const OnboardingUserRequestEditModal = ({ show, handleClose, data, onSaved }: Props) => {
    const { isTravelModuleAccessAvailable } = useAppSelector(selectAuth)
    const draftId = Number(data?.userDraftID || data?.id || 0)
    const {
        data: draftResponse,
        isFetching: isLoadingDraft,
        isError: isDraftError,
        refetch: refetchDraft,
    } = useGetUserDraftByIdQuery(draftId, {
        skip: !show || !draftId,
        refetchOnMountOrArgChange: true,
    })

    useEffect(() => {
        if (show && draftId) refetchDraft()
    }, [show, draftId, refetchDraft])

    const [updateUserDraft, { isLoading }] = useUpdateUserDraftMutation()
    const { data: departmentLookup } = useDepartmentsLookupQuery('', { skip: !show })
    const { data: designationLookup } = useGetAllDesignationWithoutRoleQuery('', { skip: !show })
    const { data: locationsData } = useGetAllLocationsQuery('', { skip: !show })
    const { data: getAlldivision } = useGetAllDivisionQuery('', { skip: !show })
    const { data: timeZoneLookUp } = useGetAllTimeZoneQuery('', { skip: !show })
    const { data: userTypeLookup } = useGetAllUserTypeLookupQuery('', { skip: !show })
    const { data: expenseTypeLookup } = useGetAllLst_UserExpenseTypeQuery('', { skip: !show })

    const libraries: Libraries = useMemo(() => ['places'], [])
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: baseUrl.REACT_APP_GOOGLE_MAPS_API_KEY,
        libraries,
    })
    const originRef = useRef<HTMLInputElement>(null)
    const originAutocompleteRef = useRef<google.maps.places.Autocomplete>()
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()

    const [form, setForm] = useState<any>({})
    const [managerOptions, setManagerOptions] = useState<{ value: any; label: string }[]>([])
    const [isSearchingManager, setIsSearchingManager] = useState(false)

    useEffect(() => {
        const draft = draftResponse?.result
        if (!show || !draft) return

        const managerEmail = draft?.managerEmail || ''
        const managerId = normalizeId(draft?.managerId)
        const managerName = draft?.managerName?.trim() || ''
        setForm({
            id: draft?.id || draft?.userDraftID || draftId,
            firstName: draft?.firstName || '',
            lastName: draft?.lastName || '',
            userEmail: draft?.userEmail || '',
            cellPhone: draft?.cellPhone || '',
            businessPhone: draft?.businessPhone || '',
            dayforceId: draft?.dayforceId || '',
            managerEmail,
            managerId,
            departmentId: normalizeId(draft?.department?.id ?? draft?.departmentId),
            divisionId: normalizeId(draft?.division?.id ?? draft?.divisionId),
            designationId: normalizeId(draft?.designation?.id ?? draft?.designationId),
            locationId: normalizeId(draft?.location?.id ?? draft?.locationId),
            userTimeZoneId: normalizeId(draft?.userTimeZone?.id ?? draft?.userTimeZoneId),
            userTypeId: normalizeId(draft?.userType?.id ?? draft?.userTypeId),
            accountTypeId: normalizeId(draft?.accountTypeId ?? draft?.expenseType?.id),
            flexBudget: draft?.flexBudget ?? 0,
            findAddress: draft?.findAddress || '',
            addressLine1: draft?.addressLine1 || '',
            addressLine2: draft?.addressLine2 || '',
            city: draft?.city || '',
            state: draft?.state || '',
            country: draft?.country?.title || draft?.country || '',
            postalCode: draft?.postalCode || '',
            latitude: draft?.latitude || '',
            longitude: draft?.longitude || '',
            expensePolicyLimit: draft?.expensePolicyLimit ?? 0,
            requestTravelPortalAccess: !!draft?.requestTravelPortalAccess,
            requestCorporateCreaditCard: !!draft?.requestCorporateCreaditCard,
            spendingLimit: draft?.spendingLimit ?? 0,
            comments: draft?.comments || '',
            apiOrgName: draft?.apiOrgName || '',
        })
        if (managerId) {
            const label = managerName && managerEmail
                ? `${managerName} (${managerEmail})`
                : (managerEmail || managerName || String(managerId))
            setManagerOptions([{ value: managerId, label }])
        } else {
            setManagerOptions([])
        }
    }, [show, draftResponse, draftId])

    const setField = (key: string, value: any) => setForm((prev: any) => ({ ...prev, [key]: value }))

    const searchManager = async (searchTerm: string) => {
        if (searchTerm.length < 2) return
        setIsSearchingManager(true)
        try {
            const response = await axios.get(
                `${baseUrl.MAIN_BASE_URL}/api/Account/GetAllManagersAndAdminsByString?srchString=${encodeURIComponent(searchTerm)}`,
            )
            if (response.data?.result) {
                setManagerOptions(
                    (response.data.result || []).map((o: any) => ({
                        value: o.id,
                        label: o.value || o.email || String(o.id),
                    })),
                )
            }
        } catch {
            // keep current options
        } finally {
            setIsSearchingManager(false)
        }
    }

    const applyPlaceToForm = (place: google.maps.places.PlaceResult) => {
        if (!place?.geometry?.location) return
        const lat = place.geometry.location.lat().toString()
        const lng = place.geometry.location.lng().toString()
        const formattedAddress = place.formatted_address ?? ''

        let city = ''
        let country = ''
        let postalCode = ''
        let state = ''
        let streetNumber = ''
        let route = ''

        place.address_components?.forEach(component => {
            if (component.types.includes('street_number')) streetNumber = component.long_name
            if (component.types.includes('route')) route = component.long_name
            if (component.types.includes('locality')) city = component.long_name
            if (component.types.includes('sublocality_level_1') && !city) city = component.long_name
            if (component.types.includes('country')) country = component.long_name
            if (component.types.includes('postal_code')) postalCode = component.long_name
            if (component.types.includes('administrative_area_level_1')) state = component.long_name
        })

        const addressLine1 = `${streetNumber} ${route}`.trim() || formattedAddress
        setForm((prev: any) => ({
            ...prev,
            findAddress: formattedAddress,
            addressLine1,
            city,
            state,
            country,
            postalCode,
            latitude: lat,
            longitude: lng,
        }))
    }

    const handleStartPlaceChanged = () => {
        const place = originAutocompleteRef.current?.getPlace()
        if (place) applyPlaceToForm(place)
    }

    const mapOptions = (list: any[]) =>
        (list || []).map((o: any) => ({
            value: o.id,
            label: o.value || o.title || o.name || String(o.id),
        }))

    const designationOptions = useMemo(() => mapOptions(designationLookup?.result || []), [designationLookup])
    const departmentOptions = useMemo(() => mapOptions(departmentLookup?.result || []), [departmentLookup])
    const locationOptions = useMemo(() => mapOptions(locationsData?.result || []), [locationsData])
    const divisionOptions = useMemo(() => mapOptions(getAlldivision?.result || []), [getAlldivision])
    const timeZoneOptions = useMemo(() => mapOptions(timeZoneLookUp?.result || []), [timeZoneLookUp])
    const userTypeOptions = useMemo(() => {
        const fromApi = mapOptions(userTypeLookup?.result || [])
        return fromApi.length > 0 ? fromApi : FALLBACK_USER_TYPE_OPTIONS
    }, [userTypeLookup])
    const expenseAccountTypeOptions = useMemo(() => {
        const fromApi = mapOptions(expenseTypeLookup?.result || [])
        return fromApi.length > 0 ? fromApi : FALLBACK_EXPENSE_ACCOUNT_TYPE_OPTIONS
    }, [expenseTypeLookup])

    const findOption = (options: { value: any; label: string }[], id: any) => {
        if (id == null || id === '') return null
        return options.find(o => String(o.value) === String(id)) || null
    }

    const selectedExpenseLabel = findOption(expenseAccountTypeOptions, form.accountTypeId)?.label || ''
    const showFlexBudget = /flex/i.test(selectedExpenseLabel) || form.accountTypeId === 2 || form.accountTypeId === 3

    const handleSave = async () => {
        if (!form?.firstName?.trim() || !form?.lastName?.trim()) {
            toast.error('First name and last name are required')
            return
        }
        if (!form?.userTypeId) {
            toast.error('User Type is required.')
            return
        }
        if (!form?.accountTypeId) {
            toast.error('Expense Account Type is required.')
            return
        }
        if (!form?.userTimeZoneId) {
            toast.error('Time Zone is required.')
            return
        }
        if (!form?.managerId) {
            toast.error('Manager is required.')
            return
        }
        if (!form?.findAddress?.trim() || !form?.addressLine1?.trim() || !form?.city?.trim()) {
            toast.error('Please correct address using Find Address and select a result from the map suggestions. Address Line 1 and City must be filled from the selected address.')
            return
        }
        if (form.requestCorporateCreaditCard) {
            if (!form.locationId) {
                toast.error('Ship To Address (location) is required for Corporate Credit Card request.')
                return
            }
            if (!form.divisionId) {
                toast.error('Division is required for Corporate Credit Card request.')
                return
            }
            if (!Number(form.spendingLimit) || Number(form.spendingLimit) <= 0) {
                toast.error('Corp CC Limit must be greater than 0 for Corporate Credit Card request.')
                return
            }
            if (!form.findAddress?.trim() || !form.addressLine1?.trim()) {
                toast.error('Please correct address using Find Address and select a map result. This address will be used for your Corporate Credit Card for shipping.')
                return
            }
        }

        try {
            const payload = {
                id: form.id,
                firstName: form.firstName,
                lastName: form.lastName,
                cellPhone: form.cellPhone,
                businessPhone: form.businessPhone,
                dayforceId: form.dayforceId,
                managerEmail: form.managerEmail,
                managerId: form.managerId,
                departmentId: form.departmentId,
                divisionId: form.divisionId,
                designationId: form.designationId,
                locationId: form.locationId,
                userTimeZoneId: form.userTimeZoneId,
                userTypeId: form.userTypeId,
                accountTypeId: form.accountTypeId,
                flexBudget: showFlexBudget ? Number(form.flexBudget) || 0 : null,
                findAddress: form.findAddress,
                addressLine1: form.addressLine1,
                addressLine2: form.addressLine2,
                city: form.city,
                state: form.state,
                expensePolicyLimit: Number(form.expensePolicyLimit) || 0,
                requestTravelPortalAccess: isTravelModuleAccessAvailable ? form.requestTravelPortalAccess : false,
                requestCorporateCreaditCard: form.requestCorporateCreaditCard,
                spendingLimit: form.requestCorporateCreaditCard ? Number(form.spendingLimit) : null,
                comments: form.comments,
                apiOrgName: form.apiOrgName,
            }
            const res: any = await updateUserDraft(payload).unwrap()
            if (res?.success) {
                toast.success(res?.message || 'User request updated successfully')
                onSaved?.()
                handleClose()
            } else {
                toast.error(res?.message || 'Unable to update user request')
            }
        } catch (e: any) {
            toast.error(e?.data?.message || 'Unable to update user request')
        }
    }

    return (
        <Modal show={show} onHide={handleClose} dialogClassName='modal-dialog modal-dialog-centered mw-1000px'>
            <div className='modal-header py-3'>
                <h2 className='fw-bolder fs-4 mb-0'>
                    Edit Onboarding User Request
                    {form?.firstName ? ` (${form.firstName} ${form.lastName || ''})` : ''}
                </h2>
                <div className='btn btn-icon btn-sm btn-active-icon-primary' onClick={handleClose}>
                    <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
                </div>
            </div>
            <div className='modal-body scroll-y mx-lg-4 my-2 py-3'>
                {isLoadingDraft && (
                    <div className='d-flex justify-content-center align-items-center py-10'>
                        <Spinner animation='border' variant='primary' />
                    </div>
                )}
                {!isLoadingDraft && isDraftError && (
                    <Alert variant='danger'>Unable to load user request for edit.</Alert>
                )}
                {!isLoadingDraft && !isDraftError && form?.id && (
                    <>
                        <Alert variant='primary' className='mb-3 py-2'>
                            Email cannot be changed for an onboarding request. Update other details as needed before approval.
                        </Alert>

                        <h5 className='fw-bold text-gray-800 mb-3 border-bottom pb-2'>Account</h5>
                        <div className='row g-3 mb-2'>
                            <div className='col-6 col-md-3'>
                                <label className='form-label fw-bold fs-7 mb-1 required'>Expense Account Type</label>
                                <Select
                                    isClearable
                                    classNamePrefix='react-select'
                                    options={expenseAccountTypeOptions}
                                    value={findOption(expenseAccountTypeOptions, form.accountTypeId)}
                                    onChange={(opt: any) => {
                                        setField('accountTypeId', opt?.value ?? null)
                                        if (!opt || !/flex/i.test(opt.label || '')) setField('flexBudget', 0)
                                    }}
                                    placeholder='Select Expense Account Type'
                                />
                            </div>
                            {showFlexBudget && (
                                <div className='col-6 col-md-3'>
                                    <TextField
                                        label='User Flex Budget $'
                                        type='number'
                                        value={String(form.flexBudget ?? 0)}
                                        onChange={(e: any) => setField('flexBudget', e.target.value)}
                                    />
                                </div>
                            )}
                            <div className='col-6 col-md-3'>
                                <label className='form-label fw-bold fs-7 mb-1 required'>User Type</label>
                                <Select
                                    isClearable
                                    classNamePrefix='react-select'
                                    options={userTypeOptions}
                                    value={findOption(userTypeOptions, form.userTypeId)}
                                    onChange={(opt: any) => setField('userTypeId', opt?.value ?? null)}
                                    placeholder='Select User Type'
                                />
                            </div>
                            <div className='col-6 col-md-3'>
                                <TextField label='Email' value={form.userEmail || ''} disabled />
                            </div>
                            <div className='col-6 col-md-3'>
                                <label className='form-label fw-bold fs-7 mb-1 d-block'>Corporate Credit Card</label>
                                <label className='form-check form-check-custom form-check-solid mt-2'>
                                    <input
                                        className='form-check-input'
                                        type='checkbox'
                                        checked={!!form.requestCorporateCreaditCard}
                                        onChange={(e) => {
                                            setField('requestCorporateCreaditCard', e.target.checked)
                                            if (!e.target.checked) setField('spendingLimit', 0)
                                        }}
                                    />
                                    <span className='form-check-label fs-7'>Request Corporate Credit Card</span>
                                </label>
                            </div>
                            {!!form.requestCorporateCreaditCard && (
                                <div className='col-6 col-md-3'>
                                    <TextField
                                        label='Corp CC Limit'
                                        required
                                        type='number'
                                        value={String(form.spendingLimit ?? 0)}
                                        onChange={(e: any) => setField('spendingLimit', e.target.value)}
                                    />
                                </div>
                            )}
                            {!!isTravelModuleAccessAvailable && (
                            <div className='col-6 col-md-3'>
                                <label className='form-label fw-bold fs-7 mb-1 d-block'>Travel Portal</label>
                                <label className='form-check form-check-custom form-check-solid mt-2'>
                                    <input
                                        className='form-check-input'
                                        type='checkbox'
                                        checked={!!form.requestTravelPortalAccess}
                                        onChange={(e) => setField('requestTravelPortalAccess', e.target.checked)}
                                    />
                                    <span className='form-check-label fs-7'>Request Travel Portal Access</span>
                                </label>
                            </div>
                            )}
                        </div>

                        <h5 className='fw-bold text-gray-800 mb-3 mt-3 border-bottom pb-2'>Personal Details</h5>
                        <div className='row g-3 mb-2'>
                            <div className='col-6 col-md-3'>
                                <TextField label='First Name' required value={form.firstName || ''} onChange={(e: any) => setField('firstName', e.target.value)} />
                            </div>
                            <div className='col-6 col-md-3'>
                                <TextField label='Last Name' required value={form.lastName || ''} onChange={(e: any) => setField('lastName', e.target.value)} />
                            </div>
                            <div className='col-6 col-md-3'>
                                <TextField label='Cell Phone' value={form.cellPhone || ''} onChange={(e: any) => setField('cellPhone', e.target.value)} />
                            </div>
                            <div className='col-6 col-md-3'>
                                <TextField label='Business Phone' value={form.businessPhone || ''} onChange={(e: any) => setField('businessPhone', e.target.value)} />
                            </div>
                            <div className='col-6 col-md-3'>
                                <SearchSelect
                                    label={labelKey.timeZone}
                                    required
                                    options={[
                                        { value: 0, label: 'Select Time Zone' },
                                        ...timeZoneOptions,
                                    ]}
                                    placeholder='Select Time Zone'
                                    value={findOption(timeZoneOptions, form.userTimeZoneId)}
                                    onChange={(opt: any) => setField('userTimeZoneId', opt?.value || null)}
                                />
                            </div>
                            <div className='col-6 col-md-3'>
                                <TextField label='Dayforce ID' required value={form.dayforceId || ''} onChange={(e: any) => setField('dayforceId', e.target.value)} />
                            </div>
                            <div className='col-6 col-md-3'>
                                <TextField
                                    label='Expense Policy Limit'
                                    type='number'
                                    value={String(form.expensePolicyLimit ?? 0)}
                                    onChange={(e: any) => setField('expensePolicyLimit', e.target.value)}
                                />
                            </div>
                        </div>

                        <h5 className='fw-bold text-gray-800 mb-3 mt-3 border-bottom pb-2'>Office Details</h5>
                        <div className='row g-3 mb-2'>
                            <div className='col-6 col-md-4'>
                                <label className='form-label fw-bold fs-7 mb-1 required'>Manager Email</label>
                                <Select
                                    isClearable
                                    isLoading={isSearchingManager}
                                    classNamePrefix='react-select'
                                    options={managerOptions}
                                    value={findOption(managerOptions, form.managerId)}
                                    placeholder='Type to search manager email'
                                    onInputChange={(inputValue) => {
                                        if (debounceRef.current) clearTimeout(debounceRef.current)
                                        debounceRef.current = setTimeout(() => {
                                            if (inputValue.length >= 2) searchManager(inputValue)
                                        }, 300)
                                    }}
                                    onChange={(opt: any) => {
                                        setField('managerId', opt?.value ?? null)
                                        const label = opt?.label ?? ''
                                        const emailFromLabel = label.includes('@')
                                            ? (label.includes('-') && /^\d+-/.test(label) ? label.substring(label.indexOf('-') + 1) : label.replace(/^.*\(([^)]+)\)\s*$/, '$1'))
                                            : ''
                                        setField('managerEmail', emailFromLabel || label)
                                    }}
                                    noOptionsMessage={({ inputValue }) =>
                                        inputValue.length < 2 ? 'Type at least 2 characters to search' : 'No managers found'
                                    }
                                />
                            </div>
                            <div className='col-6 col-md-4'>
                                <label className='form-label fw-bold fs-7 mb-1 required'>Office Location</label>
                                <Select
                                    isClearable
                                    options={locationOptions}
                                    value={findOption(locationOptions, form.locationId)}
                                    onChange={(opt: any) => setField('locationId', opt?.value ?? null)}
                                    placeholder='Select Location'
                                />
                            </div>
                            <div className='col-6 col-md-4'>
                                <label className='form-label fw-bold fs-7 mb-1 required'>Division</label>
                                <Select
                                    isClearable
                                    classNamePrefix='react-select'
                                    options={divisionOptions}
                                    value={findOption(divisionOptions, form.divisionId)}
                                    onChange={(opt: any) => setField('divisionId', opt?.value ?? null)}
                                    placeholder='Select Division'
                                />
                            </div>
                            <div className='col-6 col-md-4'>
                                <label className='form-label fw-bold fs-7 mb-1'>Department</label>
                                <Select
                                    isClearable
                                    options={departmentOptions}
                                    value={findOption(departmentOptions, form.departmentId)}
                                    onChange={(opt: any) => setField('departmentId', opt?.value ?? null)}
                                    placeholder='Select Department'
                                />
                            </div>
                            <div className='col-6 col-md-4'>
                                <label className='form-label fw-bold fs-7 mb-1'>Designation</label>
                                <Select
                                    isClearable
                                    options={designationOptions}
                                    value={findOption(designationOptions, form.designationId)}
                                    onChange={(opt: any) => setField('designationId', opt?.value ?? null)}
                                    placeholder='Select Designation'
                                />
                            </div>
                        </div>

                        <h5 className='fw-bold text-gray-800 mb-3 mt-3 border-bottom pb-2'>Address</h5>
                        <div className='row g-3'>
                            <div className='col-12'>
                                <Alert variant='primary' className='mb-3 py-2'>
                                    <p className='fs-7 mb-1 fw-semibold'>Please correct address using the Find Address field and select a result from the map suggestions.</p>
                                    <p className='fs-7 mb-0'>
                                        After you select an address, <b>Address Line 1</b>, <b>City</b>, <b>State</b>, <b>Country</b>, <b>Postal Code</b>, <b>Latitude</b>, and <b>Longitude</b> are filled automatically.
                                        You can update only <b>Address Line 2</b> after searching your address on the map.
                                    </p>
                                </Alert>
                                {!!form.requestCorporateCreaditCard && (
                                    <Alert variant='warning' className='mb-3 py-2'>
                                        <p className='fs-7 mb-0'>
                                            This address will be used for your <b>Corporate Credit Card</b> for shipping. Please verify the Find Address selection carefully before saving.
                                        </p>
                                    </Alert>
                                )}
                                <label className='form-label fw-bold fs-7 mb-1 required'>Find Address</label>
                                {isLoaded ? (
                                    <Autocomplete
                                        onLoad={(autocomplete) => {
                                            originAutocompleteRef.current = autocomplete
                                            autocomplete.addListener('place_changed', handleStartPlaceChanged)
                                        }}
                                        onPlaceChanged={() => { }}
                                    >
                                        <input
                                            type='text'
                                            className='form-control form-control-solid form-control-sm'
                                            placeholder='Type and select an address from the suggestions'
                                            ref={originRef}
                                            value={form.findAddress || ''}
                                            onChange={(e) => {
                                                setForm((prev: any) => ({
                                                    ...prev,
                                                    findAddress: e.target.value,
                                                    addressLine1: '',
                                                    city: '',
                                                    state: '',
                                                    country: '',
                                                    postalCode: '',
                                                    latitude: '',
                                                    longitude: '',
                                                }))
                                            }}
                                        />
                                    </Autocomplete>
                                ) : (
                                    <TextField label='' value={form.findAddress || ''} onChange={(e: any) => setField('findAddress', e.target.value)} />
                                )}
                                <div className='form-text text-muted mt-1'>
                                    Search and select a valid address from the dropdown. Do not type a free-form address without selecting a map result.
                                </div>
                            </div>
                            <div className='col-6 col-md-4 col-lg-3'>
                                <TextField label='Address Line 1' value={form.addressLine1 || ''} disabled />
                            </div>
                            <div className='col-6 col-md-4 col-lg-3'>
                                <TextField label='Address Line 2 (optional)' value={form.addressLine2 || ''} onChange={(e: any) => setField('addressLine2', e.target.value)} placeholder='Apartment, suite, unit, etc.' />
                            </div>
                            <div className='col-6 col-md-4 col-lg-2'>
                                <TextField label='City' value={form.city || ''} disabled />
                            </div>
                            <div className='col-6 col-md-4 col-lg-2'>
                                <TextField label='State' value={form.state || ''} disabled />
                            </div>
                            <div className='col-6 col-md-4 col-lg-2'>
                                <TextField label='Country' value={form.country || ''} disabled />
                            </div>
                            <div className='col-6 col-md-4 col-lg-2'>
                                <TextField label='Postal Code' value={form.postalCode || ''} disabled />
                            </div>
                            <div className='col-6 col-md-4 col-lg-2'>
                                <TextField label='Latitude' value={form.latitude || ''} disabled />
                            </div>
                            <div className='col-6 col-md-4 col-lg-2'>
                                <TextField label='Longitude' value={form.longitude || ''} disabled />
                            </div>
                            <div className='col-12'>
                                <label className='form-label fw-bold fs-7 mb-1'>{labelKey.comments}</label>
                                <textarea
                                    className='form-control form-control-solid form-control-sm'
                                    rows={2}
                                    value={form.comments || ''}
                                    onChange={(e) => setField('comments', e.target.value)}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>
            <div className='modal-footer py-3'>
                <button type='button' className='btn btn-light btn-sm' onClick={handleClose}>{labelKey.cancel}</button>
                <button
                    type='button'
                    className='btn btn-primary btn-sm'
                    onClick={handleSave}
                    disabled={isLoading || isLoadingDraft || !form?.id}
                >
                    {isLoading ? 'Please wait...' : 'Save User Request Details'}
                </button>
            </div>
        </Modal>
    )
}

export default OnboardingUserRequestEditModal
